import { keycloak } from "./keycloak";

const TOKEN_REFRESH_MIN_VALIDITY_SECONDS = 30;

async function ensureFreshToken(): Promise<string> {
  await keycloak.updateToken(TOKEN_REFRESH_MIN_VALIDITY_SECONDS);
  if (!keycloak.token) throw new Error("No Keycloak token after refresh");
  return keycloak.token;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The message worth showing a user.
 *
 * The API puts its explanation in the JSON body (`{ error: "..." }`), while
 * ApiError.message is only the status line. Showing the latter turns
 * "Only lara@twentyfirstgroup.com can delete this report" into
 * "403 Forbidden", which tells the user nothing and generates a support ping.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | undefined;
    if (body && typeof body.error === "string" && body.error.trim()) return body.error;
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await ensureFreshToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401) {
    void keycloak.login();
    throw new ApiError(401, "Session expired");
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
